import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useRouteWaypoints, RouteWaypoint } from "@/hooks/useRouteWaypoints";
import { MapPin, Plus, Edit, Trash2, Image, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface RouteWaypointsManagerProps {
  routeId: string;
}

const waypointTypeLabels = {
  'poi': 'Point of Interest',
  'hazard': 'Hazard',
  'rest_stop': 'Rest Stop',
  'scenic': 'Scenic View',
  'technical': 'Technical Section',
  'camping': 'Camping',
  'water': 'Water Source',
  'fuel': 'Fuel Station'
};

const waypointTypeColors = {
  'poi': 'bg-blue-500',
  'hazard': 'bg-red-500',
  'rest_stop': 'bg-green-500',
  'scenic': 'bg-purple-500',
  'technical': 'bg-orange-500',
  'camping': 'bg-teal-500',
  'water': 'bg-cyan-500',
  'fuel': 'bg-yellow-500'
};

export const RouteWaypointsManager: React.FC<RouteWaypointsManagerProps> = ({ routeId }) => {
  const { waypoints, isLoading, error, addWaypoint, updateWaypoint, deleteWaypoint } = useRouteWaypoints(routeId);
  const { toast } = useToast();
  
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    waypoint_type: 'poi' as RouteWaypoint['waypoint_type'],
    waypoint_order: 1,
    // For now, we'll use default coordinates - in a real app this would be map-picked
    geom: { type: 'Point', coordinates: [-118.2437, 34.0522] } // LA coordinates as default
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const waypointData = {
      ...formData,
      route_id: routeId,
      photo_urls: []
    };

    let result;
    if (editingId) {
      result = await updateWaypoint(editingId, waypointData);
    } else {
      result = await addWaypoint(waypointData);
    }

    if (result.error) {
      toast({
        title: "Error",
        description: `Failed to ${editingId ? 'update' : 'add'} waypoint`,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: `Waypoint ${editingId ? 'updated' : 'added'} successfully`,
      });
      setIsAdding(false);
      setEditingId(null);
      setFormData({
        name: '',
        description: '',
        waypoint_type: 'poi',
        waypoint_order: waypoints.length + 1,
        geom: { type: 'Point', coordinates: [-118.2437, 34.0522] }
      });
    }
  };

  const handleEdit = (waypoint: RouteWaypoint) => {
    setFormData({
      name: waypoint.name,
      description: waypoint.description || '',
      waypoint_type: waypoint.waypoint_type,
      waypoint_order: waypoint.waypoint_order,
      geom: waypoint.geom
    });
    setEditingId(waypoint.id);
    setIsAdding(true);
  };

  const handleDelete = async (id: string) => {
    const result = await deleteWaypoint(id);
    if (result.error) {
      toast({
        title: "Error",
        description: "Failed to delete waypoint",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Waypoint deleted successfully",
      });
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading waypoints...</div>;
  }

  if (error) {
    return <div className="text-center py-4 text-destructive">Error: {error}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Route Waypoints ({waypoints.length})
          </span>
          {!isAdding && (
            <Button 
              onClick={() => {
                setIsAdding(true);
                setFormData(prev => ({ ...prev, waypoint_order: waypoints.length + 1 }));
              }}
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Waypoint
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add/Edit Form */}
        {isAdding && (
          <Card className="border-dashed">
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">
                    {editingId ? 'Edit Waypoint' : 'Add New Waypoint'}
                  </h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsAdding(false);
                      setEditingId(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Waypoint name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                  <div className="flex gap-2">
                    <Select 
                      value={formData.waypoint_type} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, waypoint_type: value as RouteWaypoint['waypoint_type'] }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(waypointTypeLabels).map(([key, label]) => (
                          <SelectItem key={key} value={key}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder="Order"
                      className="w-20"
                      min={1}
                      value={formData.waypoint_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, waypoint_order: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                </div>
                
                <Textarea
                  placeholder="Description (optional)"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
                
                <div className="flex gap-2">
                  <Button type="submit">
                    {editingId ? 'Update' : 'Add'} Waypoint
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => {
                      setIsAdding(false);
                      setEditingId(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Waypoints List */}
        {waypoints.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No waypoints added yet. Click "Add Waypoint" to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {waypoints.map((waypoint) => (
              <Card key={waypoint.id} className="border-l-4" style={{ borderLeftColor: waypointTypeColors[waypoint.waypoint_type] || '#6b7280' }}>
                <CardContent className="py-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{waypoint.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          {waypointTypeLabels[waypoint.waypoint_type]}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          #{waypoint.waypoint_order}
                        </Badge>
                      </div>
                      {waypoint.description && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {waypoint.description}
                        </p>
                      )}
                      {waypoint.photo_urls.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Image className="h-3 w-3" />
                          {waypoint.photo_urls.length} photo{waypoint.photo_urls.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(waypoint)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(waypoint.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};